import * as R from 'ramda';
import React, { useMemo, useState, Fragment } from 'react';
// components
import { Table } from '../../../components/table';
import TextComponent from '../../../components/text';
import ToggleBtn from '../../../components/toggle-btn';
import { LocalLoader } from '../../../components/local-loader';
// helpers/constants
import * as G from '../../../helpers';
import * as GC from '../../../constants';
// hooks
import useEditViewHook from '../../../hooks/use-edit-view';
// ui
import { Box, Flex, StickedBox, MainActionButton } from '../../../ui';
// feature payrolls
import useStatistic from '../hooks/use-statistic';
import { renderInvoices } from '../components/payroll-invoices';
import { payrollStatisticTableSettings } from '../settings/charges-table-settings';
//////////////////////////////////////////////////

const defaultReportFields = [
  GC.FIELD_FLEET_TRUCK_UNIT_ID,
  GC.FIELD_DRIVERS,
  GC.FIELD_CUSTOMER_TOTAL,
  GC.FIELD_MARGIN,
  GC.FIELD_TRIP_TOTAL,
  GC.FIELD_TOTAL_DEADHEAD_DISTANCE,
  GC.FIELD_TOTAL_TRIP_DISTANCE,
  GC.FIELD_TOTAL_DISTANCE,
  GC.FIELD_RATE_PER_DISTANCE_UNIT,
  GC.FIELD_TOLL_TOTAL,
  GC.FIELD_FUEL_TOTAL,
  GC.FIELD_MAINTENANCE_TOTAL,
  GC.FIELD_DRIVER_PAY,
  GC.FIELD_PROFIT,
];

const getReportFields = (charges, fields) => R.compose(
  G.mapIndexed((name, sequence) => ({ name, sequence })),
  R.concat(fields),
  R.map((charge) => `sumByAssessorial.${charge}`),
)(charges);

const getReport = (charges) => ({
  type: GC.VENDOR_PAYROLL_UI_REPORTS,
  fields: getReportFields(charges, defaultReportFields),
  guid: `${GC.VENDOR_PAYROLL_UI_REPORTS}PayrollStatisticReport`,
});

const getSortedReport = (fieldNames, report, charges) => ({
  ...report,
  fields: getReportFields(charges, fieldNames),
});

const getDriversInfo = (drivers) => G.createStringFromArray(
  R.map(({ lastName, firstName }) => `${firstName} ${lastName}`, drivers),
  ', ',
);

const getColumnSettings = (charges) => {
  const settings = {
    [GC.FIELD_FLEET_TRUCK_UNIT_ID]: {
      width: 200,
      name: ['titles:truck', 'titles:unit-id'],
    },
    [GC.FIELD_DRIVERS]: {
      width: 300,
      name: 'titles:drivers',
      filter: {
        type: 'string',
        getFieldUiValue: ({ drivers }) => {
          if (G.isNilOrEmpty(drivers)) return '';

          return getDriversInfo(drivers);
        },
      },
      customComponent: ({ data: { drivers } }) => {
        if (G.isNilOrEmpty(drivers)) return null;

        const text = getDriversInfo(drivers);

        return (
          <TextComponent title={text} display='block' maxWidth='100%' withEllipsis={true}>
            {text}
          </TextComponent>
        );
      },
    },
    [GC.FIELD_CUSTOMER_TOTAL]: {
      width: 150,
      type: GC.FIELD_CURRENCY,
      name: 'titles:customer-total',
      filter: { type: 'number', shouldRound: true },
    },
    [GC.FIELD_MARGIN]: {
      width: 150,
      name: 'titles:margin',
      type: GC.FIELD_CURRENCY,
      filter: { type: 'number', shouldRound: true },
    },
    [GC.FIELD_TRIP_TOTAL]: {
      width: 150,
      type: GC.FIELD_CURRENCY,
      name: 'titles:trip-total',
      filter: { type: 'number', shouldRound: true },
    },
    [GC.FIELD_TOTAL_DEADHEAD_DISTANCE]: {
      width: 150,
      withoutConversion: true,
      type: GC.FIELD_DISTANCE,
      name: 'titles:deadhead-distance',
      filter: { type: 'number', shouldRound: true },
    },
    [GC.FIELD_TOTAL_TRIP_DISTANCE]: {
      width: 150,
      withoutConversion: true,
      type: GC.FIELD_DISTANCE,
      name: 'titles:total-trip-distance',
      filter: { type: 'number', shouldRound: true },
    },
    [GC.FIELD_TOTAL_DISTANCE]: {
      width: 150,
      withoutConversion: true,
      type: GC.FIELD_DISTANCE,
      name: 'titles:total-distance',
      filter: { type: 'number', shouldRound: true },
    },
    [GC.FIELD_RATE_PER_DISTANCE_UNIT]: {
      width: 150,
      type: GC.FIELD_CURRENCY,
      filter: { type: 'number', shouldRound: true },
      name: G.ifElse(G.isImperialUomSystem(), 'titles:rate-per-mile', 'titles:rate-per-kilometer'),
    },
    [GC.FIELD_TOLL_TOTAL]: {
      width: 150,
      type: GC.FIELD_CURRENCY,
      name: 'titles:toll-charges',
      filter: { type: 'number', shouldRound: true },
    },
    [GC.FIELD_FUEL_TOTAL]: {
      width: 150,
      type: GC.FIELD_CURRENCY,
      name: 'titles:fuel-charges',
      filter: { type: 'number', shouldRound: true },
    },
    [GC.FIELD_MAINTENANCE_TOTAL]: {
      width: 150,
      divider: ' ',
      type: GC.FIELD_CURRENCY,
      name: ['titles:maintenance', 'titles:total'],
      filter: { type: 'number', shouldRound: true },
    },
    [GC.FIELD_DRIVER_PAY]: {
      width: 150,
      type: GC.FIELD_CURRENCY,
      name: 'titles:total-driver-pay',
      filter: { type: 'number', shouldRound: true },
    },
    [GC.FIELD_PROFIT]: {
      width: 150,
      name: 'titles:profit',
      type: GC.FIELD_CURRENCY,
      filter: { type: 'number', shouldRound: true },
    },
  };

  const chargeSettings = R.compose(
    R.indexBy(R.prop('charge')),
    R.map((charge) => ({
      width: 200,
      useCustomName: true,
      name: `CHRG: ${charge}`,
      type: GC.FIELD_CURRENCY,
      filter: { type: 'number' },
      charge: `sumByAssessorial.${charge}`,
    })),
  )(charges);

  return R.mergeRight(settings, chargeSettings);
};

const PayrollStatistic = ({ parentProps: { statistic, statisticCurrency } }) => {
  const charges = R.compose(
    R.uniq,
    R.flatten,
    R.map(({ charges }) => R.keys(charges)),
  )(statistic);

  return (
    <Box pr={50} width='100%'>
      <Table
        itemList={statistic}
        report={getReport(charges)}
        columnSettings={getColumnSettings(charges)}
        callbackData={{ [GC.FIELD_CURRENCY]: statisticCurrency }}
        tableSettings={{ checkBoxCellWidth: 0, allowSelectItems: false }}
      />
    </Box>
  );
};

const PayrollStatisticWithInvoices = (props) => {
  const {
    data,
    callbackData: { exportStatisticRequest },
    parentProps: { guid, statistic, detailsWidth, detailsLoading, statisticLoading },
  } = props;

  const [showInvoices, setShowInvoices] = useState(false);

  const withInvoices = G.isNotNilAndNotEmpty(data);
  const withStatistic = G.isNotNilAndNotEmpty(statistic);

  return (
    <StickedBox left='0px' width={detailsWidth} display='inline-block'>
      <LocalLoader localLoaderOpen={R.or(detailsLoading, statisticLoading)}>
        <Flex
          p={15}
          width='100%'
          flexWrap='wrap'
          alignItems='baseline'
          borderBottom='1px solid'
          borderColor={G.getTheme('tables.rows.borderColor')}
        >
          {withStatistic && <PayrollStatistic {...props} />}
          {
            withStatistic &&
            <Flex mt={10} width='100%' mb={G.ifElse(showInvoices, 15, '0')}>
              <MainActionButton mr={20} height={25} onClick={() => exportStatisticRequest(R.of(Array, guid))}>
                {G.getWindowLocale('actions:download-statistic', 'Download Statistic')}
              </MainActionButton>
              {
                withInvoices &&
                <ToggleBtn
                  height={23}
                  width={100}
                  fontSize={12}
                  checked={showInvoices}
                  onToggleHandler={setShowInvoices}
                  title={G.getWindowLocale('titles:show-invoices', 'Show Invoices')}
                />
              }
            </Flex>
          }
          {R.or(G.isNilOrEmpty(statistic), R.and(withInvoices, showInvoices)) && renderInvoices(props)}
        </Flex>
      </LocalLoader>
    </StickedBox>
  );
};

const VendorPayrollStatisticTab = (props) => {
  const { maxHeight, payrollGuid, exportStatisticRequest } = props;

  const { statistic, statisticCurrency } = useStatistic({ payrollGuid });

  const tableSettings = R.assoc('maxHeight', maxHeight, payrollStatisticTableSettings);

  const list = R.compose(
    R.uniq,
    R.flatten,
    R.map(({ charges }) => R.keys(charges)),
  )(statistic);

  const report = getReport(list);
  const columnSettings = getColumnSettings(list);

  const { uiReportFields, handleOpenEditView } = useEditViewHook({
    ...props,
    report,
    columnSettings,
    defaultReportFields,
  });

  const sortedReport = useMemo(() => (
    G.isNilOrEmpty(uiReportFields) ?
    report : getSortedReport(uiReportFields, report, list)
  ), [statistic, uiReportFields]);

  return (
    <Fragment>
      <Table
        showEditView={true}
        itemList={statistic}
        report={sortedReport}
        shouldUseUIFilters={true}
        useSearchableColumns={true}
        withResizableColumns={true}
        tableSettings={tableSettings}
        useNewTitleFilterInputs={true}
        columnSettings={getColumnSettings(list)}
        handleClickEditOrder={handleOpenEditView}
        callbackData={{ [GC.FIELD_CURRENCY]: statisticCurrency }}
        filterProps={G.getFilterPropsFromColumnSettings(columnSettings)}
      />
      <MainActionButton mt={15} ml={15} type='button' onClick={() => exportStatisticRequest(R.of(Array, payrollGuid))}>
        {G.getWindowLocale('actions:download-statistic', 'Download Statistic')}
      </MainActionButton>
    </Fragment>
  );
};

export {
  VendorPayrollStatisticTab,
  PayrollStatisticWithInvoices,
};
